import * as THREE from 'three';
import { lookLabel } from '../content/looks';
import { beenInFlag, hourLabel } from '../content/parkHomes';
import { BEARD_STYLES, HAIR_STYLES, type CharacterLook } from '../state/types';
import type { Interactions } from '../systems/Interactions';
import { signBoard } from '../world/builders/props';
import { LOTS, type Vec2 } from '../world/layout';
import { box, cyl, M, mat } from '../world/materials';
import {
  BRENDA_SPOTS,
  buildBrendaInterior,
  buildHankInterior,
  buildWandaInterior,
  HANK_SPOTS,
  WANDA_SPOTS,
  type Bunker,
  type Parlor,
  type Salon,
} from '../world/neighborRooms';
import type { PublicRoom } from '../world/PublicRoom';
import type { Activities } from './Activities';
import type { Interiors } from './Interiors';
import type { GameServices } from './services';
import type { IconToken } from '../icons/index';

const HAIR_COLORS = ['#5b3a1e', '#1b1b1b', '#e8c070', '#ffd700', '#a8321e', '#d9d9d9', '#ff6fa8', '#7a5cff'];
const next = <T>(list: readonly T[], cur: T): T => list[(list.indexOf(cur) + 1) % list.length] as T;

export const HAIRCUT_PRICE = 15;
export const READING_PRICE = 10;

interface Visit {
  id: 'brenda' | 'wanda' | 'hank';
  /** Where you knock (the foot of their steps). */
  stoop: Vec2;
  room: PublicRoom;
  closed: string;
}

/** The tarot: each card does a little something for the rest of the day. */
interface Card {
  name: string;
  art: IconToken;
  text: string;
  apply(s: GameServices): void;
}

const CARDS: readonly Card[] = [
  {
    name: 'The Sun',
    art: ':sun:',
    text: "'Fortune smiles on you today, child. Buy a scratch-off before the sun goes down.' (Scratchers pay DOUBLE for the rest of today.)",
    apply: (s) => s.store.setFlag('jackpot_day', s.store.day),
  },
  {
    name: 'The Star',
    art: ':star:',
    text: "'People will look at you and see something. Maybe it's you. Maybe it's the light.' (+5 Clout, and +1 Clout on everything today.)",
    apply: (s) => {
      s.store.setFlag('fresh', s.store.day);
      s.store.addClout(5);
    },
  },
  {
    name: 'The Moon',
    art: ':moon:',
    text: "'Strange lights in your sky tonight. Do not be afraid. Actually, be a little afraid.' (Something might come for you tonight, especially out in the Back Forty.)",
    apply: (s) => s.store.setFlag('ufo_omen', s.store.day),
  },
  {
    name: 'The Fool',
    art: ':cards:',
    text: "'Money at your feet, and you walk right past it. Look down, fool.' (Cans and scrap all over the park.)",
    apply: (s) => {
      s.ctx.world.spawnCans(12);
      s.ctx.world.scatterScrap(6);
    },
  },
  {
    name: 'Wheel of Fortune',
    art: ':fortune-wheel:',
    text: "'Round and round it goes. Here, take this. The spirits say it's for you.' She hands you a scratch-off from her bra. It's warm.",
    apply: (s) => s.store.give('scratchCow', 1),
  },
  {
    name: 'Death',
    art: ':skull:',
    text: "'Don't look like that. Death means change. A new start.' You feel... lighter? (+15 Health, -10 Energy.)",
    apply: (s) => s.store.apply({ stats: { health: 15, energy: -10 } }),
  },
  {
    name: 'The Lovers',
    art: ':heart-arrow:',
    text: "'Someone is thinking about you right now.' Your phone buzzes. Somebody liked your post from 2019.",
    apply: (s) => {
      s.store.post({
        author: 'Tammy Lynn',
        avatar: 'tammy',
        text: `Shoutout to ${s.store.state.player.name} :smitten: the best neighbor on Main St!! #blessed #lucky acres`,
        likes: 30,
      });
      s.store.addClout(3);
    },
  },
  {
    name: 'The Tower',
    art: ':tower:',
    text: "'Something is coming down. Hold on to your hat. And your trailer.' Wanda won't say any more. She lights a second candle for you.",
    apply: (s) => s.store.addClout(2),
  },
];

const BALL = [
  'The ball says: "Signs point to yes." Wanda says the ball is "usually drunk."',
  'The ball says: "Ask again after rent day."',
  'The ball says: "Absolutely not, Bubba."',
  'The ball says: "The Camaro will never run." Wanda covers it with a scarf. "It\'s been negative lately."',
  'The ball shows you a face. It is your own face, but with a better mullet.',
];

const RADIO = [
  "Channel 19: 'Breaker breaker, smokey's set up a speed trap by the fairgrounds.' Hank nods gravely. 'Smokey is everywhere.'",
  "A man named Duane reads numbers for ten minutes. '4. 8. 15.' Hank writes them all down. 'Lotto numbers. Or launch codes. Same thing.'",
  "Static, then: '...lights over the Back Forty again, real late, past midnight...' Hank looks at you. You look at Hank.",
  "A trucker's weather report: 'Storm cell building to the west.' Hank starts filling jugs with water. He fills them every time.",
  "Somebody plays a harmonica into the mic for six straight minutes. Hank cries a little. 'That's Earl's brother. He's in Leavenworth.'",
];

/**
 * The north end of Main Street: Brenda's Beauty Barn on Lot 12, Madame Wanda's on
 * Lot 14 and Hank's bunker on Lot 15. Knock, go in, and get a haircut, your fortune or
 * a lecture about the water tower.
 */
export class Townsfolk {
  readonly salon: Salon;
  readonly parlor: Parlor;
  readonly bunker: Bunker;
  private readonly visits: Visit[];
  private seated = false;
  /** The hair menu is about to reopen (a choice closes it first). */
  private again = false;

  constructor(
    private readonly s: GameServices,
    private readonly interiors: Interiors,
    private readonly act: Activities,
  ) {
    const { dynamic, collision } = s.world;
    this.salon = buildBrendaInterior(dynamic, collision);
    this.parlor = buildWandaInterior(dynamic, collision);
    this.bunker = buildHankInterior(dynamic, collision);
    this.visits = [
      {
        id: 'brenda',
        stoop: LOTS.brenda.stoop,
        room: this.salon.room,
        closed: "A sign on the door: 'CLOSED. AT BINGO. 9 TO 7 TOMORROW.'",
      },
      {
        id: 'wanda',
        stoop: LOTS.wanda.stoop,
        room: this.parlor.room,
        closed: "A sign on the door: 'THE SPIRITS SLEEP TILL NOON. SO DOES WANDA.'",
      },
      {
        id: 'hank',
        stoop: LOTS.hank.stoop,
        room: this.bunker.room,
        closed: 'Hank yells through the door: "PASSWORD?" You don\'t know it. Nobody does.',
      },
    ];
    for (const v of this.visits) interiors.addPublic(v.room, { x: v.stoop.x, z: v.stoop.z + 0.3 });
    this.yardSigns();
  }

  /** Each lot advertises. */
  private yardSigns(): void {
    const { world } = this.s;
    const place = (obj: THREE.Object3D, x: number, z: number, rot: number): void => {
      obj.position.set(x, 0, z);
      obj.rotation.y = rot;
      world.root.add(obj);
      world.collision.addCircle(x, z, 0.3);
    };
    const b = LOTS.brenda;
    place(
      signBoard(
        {
          w: 512,
          h: 256,
          bg: '#fff0f6',
          fg: '#c2185b',
          border: '#c2185b',
          borderWidth: 10,
          lines: [
            { text: "Brenda's", size: 70, font: 'Pacifico' },
            { text: 'BEAUTY BARN', size: 56, font: 'Anton', color: '#1b1b1b' },
            { text: 'CUTS • COLOR • MULLETS', size: 30, font: 'Anton' },
          ],
        },
        1.5,
        0.75,
        0.8,
      ),
      b.door.x - 3.2,
      b.door.z + 3.4,
      -Math.PI / 2 + 0.3,
    );
    const w = LOTS.wanda;
    place(
      signBoard(
        {
          w: 512,
          h: 256,
          bg: '#1a0d22',
          fg: '#ffd400',
          border: '#9b5de5',
          borderWidth: 10,
          lines: [
            { text: 'MADAME WANDA', size: 64, font: 'Rye' },
            { text: 'PSYCHIC • TAROT • $10', size: 40, font: 'Anton', color: '#ff9ad5' },
            { text: 'WALK-INS (SHE KNEW)', size: 30, font: 'Anton', color: '#ffffff' },
          ],
        },
        1.5,
        0.75,
        0.8,
      ),
      w.door.x + 3.2,
      w.door.z + 3.4,
      Math.PI / 2 - 0.3,
    );
    const h = LOTS.hank;
    place(
      signBoard(
        {
          w: 512,
          h: 256,
          bg: '#ffd400',
          fg: '#111111',
          border: '#111111',
          borderWidth: 12,
          lines: [
            { text: 'KEEP OUT', size: 90, font: 'Anton' },
            { text: 'THIS MEANS YOU', size: 40, font: 'Anton', color: '#c62828' },
            { text: '(AND YOU)', size: 30, font: 'Anton' },
          ],
        },
        1.3,
        0.65,
        0.8,
      ),
      h.door.x - 3.2,
      h.door.z + 3.4,
      -Math.PI / 2 + 0.3,
    );
    // Hank's antenna mast, for listening to the trucks and the sky.
    const mast = new THREE.Group();
    mast.add(cyl(0.05, 0.07, 7, M.steel, 0, 3.5, 0, 6));
    for (const y of [4.5, 5.6, 6.6]) mast.add(box(1.6 - (y - 4.5) * 0.4, 0.04, 0.04, M.steel, 0, y, 0));
    mast.add(
      new THREE.Mesh(
        new THREE.SphereGeometry(0.08, 8, 6),
        mat('#ff3030', { emissive: '#ff3030', emissiveIntensity: 1.5 }),
      ).translateY(7.05),
    );
    place(mast, h.trailer.x + 4.5, h.trailer.z - 1.2, 0);
  }

  register(interactions: Interactions): void {
    const { store, npcs, ui, rng } = this.s;
    const add = interactions.add.bind(interactions);
    for (const v of this.visits) {
      add({
        id: `knock-${v.id}`,
        at: () => v.stoop,
        radius: 1.7,
        prompt: () => {
          const npc = npcs.get(v.id);
          if (!npc) return null;
          return npc.active
            ? `Knock on ${npc.def.name}'s door`
            : `${npc.def.name}'s place (closed till ${hourLabel(npc.def.hours[0])})`;
        },
        action: () => {
          if (!npcs.get(v.id)?.active) {
            store.toast(v.closed, 'info', ':door:');
            return;
          }
          this.s.sfx('door');
          this.interiors.enterPublic(v.room.slot);
          if (this.interiors.current === v.room) store.setFlag(beenInFlag(v.id), true);
        },
        priority: 0.6,
        icon: ':door:',
      });
      add({
        id: `knock-${v.id}-exit`,
        at: () => v.room.exit,
        radius: 1.2,
        prompt: () => 'Head outside',
        action: () => this.interiors.leave(),
        priority: 0.5,
        icon: ':door:',
      });
    }

    // Brenda's chair.
    add({
      id: 'brenda-chair',
      at: () => this.salon.room.world({ x: BRENDA_SPOTS.chair.x, z: BRENDA_SPOTS.chair.z + 0.8 }),
      radius: 1.3,
      prompt: () =>
        !npcs.get('brenda')?.active
          ? null
          : store.doneToday('brenda_cut')
            ? 'Sit back down for touch-ups'
            : `Sit in Brenda's chair (cut & color, $${HAIRCUT_PRICE})`,
      action: () => this.haircut(),
      icon: ':haircut:',
      highlight: () => this.salon.chair,
    });

    // Madame Wanda's table.
    add({
      id: 'wanda-reading',
      at: () => this.parlor.room.world(WANDA_SPOTS.seat),
      radius: 1.2,
      prompt: () =>
        !npcs.get('wanda')?.active
          ? null
          : store.doneToday('wanda_read')
            ? 'Wanda: "The cards only speak once a day, sugar."'
            : `Have your fortune read ($${READING_PRICE})`,
      action: () => this.reading(),
      icon: ':crystal-ball:',
      highlight: () => this.parlor.ball,
    });
    add({
      id: 'wanda-ball',
      at: () => this.parlor.room.world({ x: WANDA_SPOTS.table.x + 0.9, z: WANDA_SPOTS.table.z + 0.4 }),
      radius: 0.9,
      prompt: () => 'Ask the crystal ball a question',
      action: () => {
        this.s.sfx('whistle');
        ui.modal.show({
          title: 'The Crystal Ball',
          art: ':crystal-ball:',
          text: rng.pick(BALL),
          choices: [],
          leave: 'Spooky',
        });
      },
      icon: ':question:',
    });
    add({
      id: 'wanda-cat',
      at: () => this.parlor.room.world({ x: WANDA_SPOTS.cat.x + 0.6, z: WANDA_SPOTS.cat.z - 0.3 }),
      radius: 1.1,
      prompt: () => 'Pet Mr. Whiskers',
      action: () => {
        this.s.sfx('yip');
        if (!store.doneToday('wanda_cat')) {
          store.markToday('wanda_cat');
          store.apply({ stats: { health: 3 } });
        }
        store.toast(
          rng.pick([
            'Mr. Whiskers purrs like an outboard motor.',
            'Mr. Whiskers bites you. Lovingly. Mostly.',
            "Mr. Whiskers stares at a spot behind you. There's nothing there. Probably.",
          ]),
          'good',
          ':black-cat:',
        );
      },
      icon: ':black-cat:',
      highlight: () => this.parlor.cat,
    });

    // Hank's bunker.
    add({
      id: 'hank-radio',
      at: () => this.bunker.room.world({ x: HANK_SPOTS.radio.x, z: HANK_SPOTS.radio.z + 0.85 }),
      radius: 1.2,
      prompt: () => (store.doneToday('hank_radio') ? 'Nothing but static' : 'Listen in on the ham radio'),
      action: () => {
        if (store.doneToday('hank_radio')) return;
        store.markToday('hank_radio');
        this.s.sfx('phone');
        store.apply({ minutes: 20, clout: 1 });
        ui.modal.show({
          title: 'Channel 19',
          art: ':ham-radio:',
          text: rng.pick(RADIO),
          choices: [],
          leave: 'Over and out',
        });
      },
      icon: ':ham-radio:',
      highlight: () => this.bunker.radio,
    });
    add({
      id: 'hank-board',
      at: () => this.bunker.room.world({ x: HANK_SPOTS.board.x, z: HANK_SPOTS.board.z + 1.0 }),
      radius: 1.2,
      prompt: () => "Study Hank's wall of evidence",
      action: () =>
        ui.modal.show({
          title: 'The Wall',
          art: ':yarn:',
          text: rng.pick([
            "Red string connects the water tower, Apex Capital, a photo of Big Tiny, and a Hardee's receipt. It makes a terrifying amount of sense.",
            "Seven photos of the same blurry light over the Back Forty. Under one, in Sharpie: 'PAST MIDNIGHT. ALWAYS PAST MIDNIGHT.'",
            'A county map. Every place a cow went missing has a pin. There are a lot of pins near the crick.',
          ]),
          choices: [],
          leave: 'I need a drink',
        }),
      icon: ':yarn:',
      highlight: () => this.bunker.board,
    });
    void this.act;
  }

  /** In Brenda's chair: a cut, a color and a beard trim, all included once you've paid. */
  private haircut(): void {
    const { store, player } = this.s;
    if (!this.act.canAct() && !this.seated) return;
    if (!store.doneToday('brenda_cut')) {
      if (!store.spend(HAIRCUT_PRICE)) {
        store.toast(
          `Brenda: "${HAIRCUT_PRICE} dollars, baby. I take cash, food stamps and gossip. Mostly cash."`,
          'bad',
          ':haircut:',
        );
        return;
      }
      store.markToday('brenda_cut');
      store.incr('haircuts');
    }
    const seat = this.salon.room.world(BRENDA_SPOTS.chair);
    player.teleport(seat.x, seat.z + 0.05, Math.PI);
    this.seated = true;
    player.doBusy({ pose: 'sit', seat: 0.55, until: Infinity, facing: Math.PI });
    this.s.sfx('scratch');
    this.hairMenu();
  }

  private hairMenu(): void {
    const { store, ui, player } = this.s;
    const look = store.state.player.look;
    const change = (patch: Partial<CharacterLook>) => () => {
      this.s.setLook({ ...store.state.player.look, ...patch });
      this.s.sfx('scratch');
      this.again = true;
      window.setTimeout(() => this.hairMenu(), 0);
    };
    ui.modal.show(
      {
        title: "Brenda's Chair",
        speaker: { id: 'brenda', name: "Brenda's Beauty Barn", voice: 340 },
        text: "Brenda snaps a cape around your neck and cracks her knuckles. 'What are we doing today, hon? And don't say \"just a trim.\" Nobody leaves my chair with just a trim.'",
        choices: [
          {
            label: `:scissors: Cut: ${lookLabel.hair(look.hair)} → ${lookLabel.hair(next(HAIR_STYLES, look.hair))}`,
            run: change({ hair: next(HAIR_STYLES, look.hair) }),
          },
          {
            label: ':paint: A new color',
            requires: () => look.hair !== 'bald' || "Brenda: 'Color on what, baby?'",
            run: change({ hairColor: next(HAIR_COLORS, look.hairColor) }),
          },
          {
            label: `:man: Beard: ${lookLabel.beard(look.beard)} → ${lookLabel.beard(next(BEARD_STYLES, look.beard))}`,
            run: change({ beard: next(BEARD_STYLES, look.beard) }),
          },
        ],
        leave: 'Love it, Brenda',
      },
      () => {
        if (this.again) {
          this.again = false;
          return;
        }
        if (!this.seated) return;
        this.seated = false;
        player.busy = null;
        const out = this.salon.room.world({ x: BRENDA_SPOTS.chair.x, z: BRENDA_SPOTS.chair.z + 0.9 });
        player.teleport(out.x, out.z, 0);
        store.addClout(store.doneToday('brenda_compliment') ? 0 : 3);
        store.markToday('brenda_compliment');
      },
    );
  }

  /** Madame Wanda turns over a card, and the day goes a little differently. */
  private reading(): void {
    const { store, rng, ui, player } = this.s;
    if (!this.act.canAct() || store.doneToday('wanda_read')) return;
    if (!store.spend(READING_PRICE)) {
      store.toast(
        'Wanda: "The spirits require ten dollars. They don\'t take checks. They\'ve been burned."',
        'bad',
        ':crystal-ball:',
      );
      return;
    }
    store.markToday('wanda_read');
    store.incr('readings');
    const seat = this.parlor.room.world(WANDA_SPOTS.seat);
    player.teleport(seat.x, seat.z, Math.PI);
    player.doBusy({ pose: 'sit', seat: 0.48, until: this.s.now() + 2.5, facing: Math.PI });
    this.s.sfx('whistle');
    const card = rng.pick(CARDS);
    card.apply(this.s);
    window.setTimeout(
      () =>
        ui.modal.show({
          title: card.name,
          art: card.art,
          speaker: { id: 'wanda', name: 'Madame Wanda', voice: 250 },
          text: `Wanda shuffles, cuts, and turns over a single card. The candles gutter. ${card.text}`,
          choices: [],
          leave: 'Thank you, Madame',
        }),
      600,
    );
  }
}
